import _ from 'lodash';
import React, { useCallback, useContext, useMemo, useRef, useState, memo } from 'react';
import { Button, Icon, Tag, TextArea, Typography } from '@scale/scaleui';
import { belongsToStep, getCanAddFeedback } from '@scale/shared/types/chat/contributorFeedback';
import IssueGroupAccordion from 'react-scale/components/scalers/tasks/ThreadedInteraction/ContributorFeedback/components/IssueGroupAccordion';
import {
  FeedbackType,
  StepParamsForType,
  StepParamsForTypeWithStage,
  truncateText,
} from '@scale/shared/types/chat';
import { StepParamsAuxiliary } from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.computed';
import { useContributorFeedbackStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ContributorFeedback/stores/useContributorFeedbackStore';
import {
  constructStepChildId,
  useWorkerCommentsStore,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useWorkerCommentsStore';
import uuid from 'uuid';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { ContributorFeedbackStepContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ContributorFeedback/context';
import { useChatStoreExtract } from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import { ReviewLevel } from '@scale/shared-types';
import { twMerge } from 'tailwind-merge';
import {
  ChatTheme,
  useChatThemeStore,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useChatThemeStore';

interface FeedbackHeaderProps {
  className?: string;
  numUnaddressedFeedback: number;
  showAddStepCommentButton: boolean;
  onStepCommentBoxOpen: () => void;
}

const FeedbackHeader: React.FC<FeedbackHeaderProps> = ({
  className,
  numUnaddressedFeedback,
  showAddStepCommentButton,
  onStepCommentBoxOpen,
}) => {
  return (
    <div
      className={twMerge(
        'flex flex-row justify-between items-center rounded-t-md gap-0.5',
        className,
      )}
    >
      <div className="flex flex-row p-1.5 gap-0.5 items-center">
        <Icon variant="black" icon={['fas', 'message-pen']} />

        <div className="flex flex-row items-center gap-1">
          <Typography.Subtitle4 className="text-xs">Reviewer feedback</Typography.Subtitle4>
          {!!numUnaddressedFeedback && (
            <Tag className="bg-danger-50" size="sm">
              <Typography.Paragraph3 className="text-[10px] text-danger-500">
                {numUnaddressedFeedback} unresolved
              </Typography.Paragraph3>
            </Tag>
          )}
        </div>
      </div>
      {showAddStepCommentButton && (
        <div className="mr-1.5">
          <Button
            className="w-16 text-neutral-900"
            variant="white"
            icon={{
              type: ['fas', 'plus'],
              className: 'text-neutral-900',
            }}
            onClick={onStepCommentBoxOpen}
          >
            Add
          </Button>
        </div>
      )}
    </div>
  );
};

interface AddCommentBoxProps {
  className?: string;
  visible: boolean;
  step: (StepParamsForType<any> & StepParamsAuxiliary) | StepParamsForTypeWithStage<any>;
  onCancel?: () => void;
  onCommentSubmit?: () => void;
}

const AddCommentBox: React.FC<AddCommentBoxProps> = ({
  className,
  visible,
  step,
  onCancel,
  onCommentSubmit,
}) => {
  const taskContext = useContext(ChatTaskContext);
  const stepWithIndexKey = constructStepChildId(step);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [commentText, setCommentText] = useState<string>('');

  const handleTextAreaInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCommentText(e.target.value);
  };

  const addWorkerComment = useWorkerCommentsStore(state => state.addWorkerComment);
  const selectWorkerComment = useWorkerCommentsStore(state => state.selectWorkerComment);
  const selection = useWorkerCommentsStore(state => state.selection);

  const discardComment = useCallback(() => {
    setCommentText('');
    selectWorkerComment(null);
    onCancel?.();
  }, [selectWorkerComment, onCancel]);

  const submitComment = useCallback(
    (commentText: string) => {
      if (commentText.length) {
        const workerCommentId = uuid();

        // If there is a selection, and that selection belongs to the current step, add
        // an inline comment. Otherwise, add a comment to the step itself.
        if (!selection.empty && belongsToStep(selection.childId, stepWithIndexKey)) {
          addWorkerComment({
            workerCommentId,
            comment: commentText,
            turn: step.turn,
            childId: selection.childId,
            title: truncateText(commentText, 35),
            locationStart: selection.from,
            locationEnd: selection.to,
            quotedText: selection.text,
            author: taskContext.workerId,
          });
        } else {
          addWorkerComment({
            workerCommentId,
            comment: commentText,
            turn: step.turn,
            childId: stepWithIndexKey,
            title: truncateText(commentText, 35),
            author: taskContext.workerId,
          });
        }
        selectWorkerComment(workerCommentId);
      }
      setCommentText('');
      onCommentSubmit?.();
    },
    [
      addWorkerComment,
      step.turn,
      selection.empty,
      selection.childId,
      selection.from,
      selection.to,
      selection.text,
      stepWithIndexKey,
      taskContext.workerId,
      selectWorkerComment,
      onCommentSubmit,
    ],
  );

  const onKeyDownSubmit = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.shiftKey && e.key === 'Enter') {
        e.preventDefault();
        submitComment(commentText);
      }
      if (e.key === 'Escape') {
        discardComment();
      }
    },
    [commentText, discardComment, submitComment],
  );

  return (
    <div className={twMerge(visible ? '' : 'hidden', className)}>
      {belongsToStep(selection.childId, stepWithIndexKey) && selection.text && (
        <div className="mx-4 mb-4 border-l-2 border-l-neutral-300">
          <Typography.Subtitle4 className="pl-2 text-neutral-500 whitespace-pre-wrap">
            {truncateText(selection.text, 100)}
          </Typography.Subtitle4>
        </div>
      )}
      <div>
        <TextArea
          className="pb-2 px-4"
          ref={textareaRef}
          id="dynamic-textarea"
          rows={1}
          onFocus={(e: React.FocusEvent<HTMLTextAreaElement>) => {
            const textArea = e.target;
            textArea.style.height = 'auto';
            textArea.style.height = textArea.scrollHeight + 'px';
            textArea.style.maxHeight = '16rem';
          }}
          autoFocus
          dynamic
          resizable={false}
          onChange={handleTextAreaInputChange}
          value={commentText}
          onKeyDown={onKeyDownSubmit}
          placeholder="Enter your comment"
        />
        <div className="flex flex-row justify-between pb-2 px-4">
          <Button
            onClick={() => {
              discardComment();
            }}
            size="sm"
            variant="neutral"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              submitComment(commentText);
            }}
            size="sm"
            variant="primary"
          >
            Comment
          </Button>
        </div>
      </div>
    </div>
  );
};
interface ContributorHumanFeedbackProps {
  className?: string;
  step: (StepParamsForType<any> & StepParamsAuxiliary) | StepParamsForTypeWithStage<any>;
}

const ContributorHumanFeedbackBox: React.FC<ContributorHumanFeedbackProps> = ({
  className,
  step,
}) => {
  const taskContext = useContext(ChatTaskContext);
  const reviewLevel = useChatStoreExtract(state => state.reviewLevel);
  const canAddFeedback = getCanAddFeedback({
    reviewLevel,
    areCommentsReadOnly: taskContext.areCommentsReadOnly,
  });
  const stepWithIndexKey = constructStepChildId(step);
  const selection = useWorkerCommentsStore(state => state.selection);
  const deleteSelection = useWorkerCommentsStore(state => state.deleteSelection);
  const [isStepCommentBoxOpen, setIsStepCommentBoxOpen] = useState<boolean>(false);
  const feedback = useContributorFeedbackStore(s =>
    s.feedback.filter(({ childId }) => belongsToStep(childId, stepWithIndexKey)),
  );
  const theme = useChatThemeStore(state => state.theme);

  const humanFeedback = useMemo(() => {
    return feedback.filter(
      feedbackItem =>
        feedbackItem.type === FeedbackType.HumanReviewerFeedback &&
        feedbackItem.childId &&
        belongsToStep(feedbackItem.childId, stepWithIndexKey),
    );
  }, [feedback, stepWithIndexKey]);

  const isReviewer = reviewLevel !== ReviewLevel.Attempt;

  const [myFeedback, existingFeedback] = useMemo(
    () => _.partition(humanFeedback, feedbackItem => feedbackItem.author === taskContext.workerId),
    [humanFeedback, taskContext.workerId],
  );

  // If CB is an L-1 attempter, then only display this component if there is actual feedback
  const shouldDisplayFeedbackComponent = isReviewer || existingFeedback.length > 0;

  const numUnaddressedFeedbackItems = useMemo(
    () => existingFeedback.filter(feedbackItem => !feedbackItem.action).length,
    [existingFeedback],
  );

  const shouldShowInlineAddCommentBox =
    belongsToStep(selection.childId, stepWithIndexKey) && !!selection.text;

  // Darkmode support is built-in for standard `bg-` tailwind classes (i.e bg-neutral-200).
  // Since we're using custom colors, we need to handle darkmode manually.
  const bgColor = theme === ChatTheme.Light ? 'bg-[#F7FAFF]' : 'bg-[#101217]';

  return (
    <>
      {shouldDisplayFeedbackComponent ? (
        <ContributorFeedbackStepContext.Provider value={{ stepWithIndexKey }}>
          <div
            className={twMerge(
              'flex flex-col lg:w-60 xl:w-80 rounded-md border border-neutral-200 overflow-hidden',
              bgColor,
              className,
            )}
          >
            <FeedbackHeader
              numUnaddressedFeedback={numUnaddressedFeedbackItems}
              showAddStepCommentButton={
                isReviewer &&
                !isStepCommentBoxOpen &&
                !shouldShowInlineAddCommentBox &&
                canAddFeedback
              }
              onStepCommentBoxOpen={() => setIsStepCommentBoxOpen(true)}
            />
            {isReviewer && (
              <>
                {/* Add Comment box for inline span tag comments */}
                <AddCommentBox
                  className={bgColor}
                  visible={shouldShowInlineAddCommentBox}
                  step={step}
                  onCancel={() => {
                    deleteSelection();
                  }}
                  onCommentSubmit={() => {
                    deleteSelection();
                  }}
                />
                {/* Add Comment box for step level comments */}
                <AddCommentBox
                  className={bgColor}
                  visible={isStepCommentBoxOpen && !shouldShowInlineAddCommentBox}
                  step={step}
                  onCancel={() => setIsStepCommentBoxOpen(false)}
                  onCommentSubmit={() => setIsStepCommentBoxOpen(false)}
                />
              </>
            )}
            <div>
              {/* An attempter won't be able to see their OWN feedback even if they attempt an L-1 task that they left a review on */}
              {isReviewer && !!myFeedback.length && (
                <IssueGroupAccordion
                  accordionClassName={bgColor}
                  headerTitle="My feedback"
                  areUnaddressedFeedbackItems={false}
                  feedbackItems={myFeedback}
                />
              )}
              {!!existingFeedback.length && (
                <IssueGroupAccordion
                  accordionClassName={bgColor}
                  // HACK: Header div isn't show if the `headerTitle` is an empty string
                  headerTitle={isReviewer ? 'Feedback from other reviewers' : ''}
                  areUnaddressedFeedbackItems={!!numUnaddressedFeedbackItems}
                  feedbackItems={existingFeedback}
                />
              )}
            </div>
          </div>
        </ContributorFeedbackStepContext.Provider>
      ) : (
        // Empty div to emulate a two-width layout
        <div className="flex lg:w-60 xl:w-80" />
      )}
    </>
  );
};

export default memo(ContributorHumanFeedbackBox);
